import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Thermometer, Droplet, Sun, Battery, Gauge, Zap, Snowflake, Flame } from "lucide-react";
import { useLang } from "../context/I18nContext";
import SectionVideo from "../components/SectionVideo";
import { SECTION_VIDEOS } from "../config/sectionVideos";

/**
 * Energie & Heizung — Wärmepumpen, PV, EMS, Warmwasser, Kühlung
 */
export default function EnergieHeizung() {
  const { t } = useLang();

  const heatingItems = [
    "Wärmepumpen (Luft)", "Fußbodenheizung", "Flächenheizung", "Niedertemperatursysteme",
    "Heizkreisregelung", "Raumregelung", "Pufferspeicher", "Warmwasserintegration",
    "Hydraulik", "Zonenregelung",
  ];
  const hotWaterItems = [
    "Warmwasserbereitung", "Speicher", "Wärmepumpenanbindung", "Zirkulation",
    "Temperaturregelung", "Zeitprogramme", "Bedarfsschaltung", "PV-Integration",
  ];
  const coolingItems = [
    "Flächenkühlung", "Deckenkühlung", "Fußbodenkühlung (soweit geeignet)",
    "Raumregelung", "Kombination mit Wärmepumpe", "Sensorik", "Zonensteuerung",
  ];
  const pvItems = [
    "PV-Anlage", "Eigenverbrauch", "Speicher", "Wärmepumpenkopplung",
    "E-Auto Lastmanagement", "Messung", "Verbrauchsvisualisierung",
  ];
  const emsItems = [
    "Verbrauchsmessung", "Monitoring", "Lastmanagement", "Eigenverbrauch",
    "PV-Überschuss", "Wärmepumpenansteuerung", "Speicherintegration",
    "Verbrauchsvisualisierung", "Betriebsdaten",
  ];

  return (
    <div className="page-shell" data-testid="energie-heizung-page">
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">02 · {t("nav.energyHeat")}</div>
        <h1 className="page-title">
          Energie <span className="italic-accent">intelligent</span> nutzen.
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          Heizung, Warmwasser und Kühlung werden nicht isoliert betrachtet — sondern im Zusammenspiel mit
          Photovoltaik, Speicher und Energiemanagement. Jede Anlage wird auf das konkrete Gebäude ausgelegt.
        </p>
      </section>

      {/* Section video — Wärmepumpe & Energie */}
      <SectionVideo {...SECTION_VIDEOS.energieHeizung}/>

      {/* Energieflussdiagramm (SVG) */}
      <section className="site-container section-block border-t border-white/10">
        <div className="mb-10 reveal">
          <div className="eyebrow mb-5">Energieflussdiagramm</div>
          <h2 className="section-title max-w-3xl">
            Von der Erzeugung bis zur <span className="italic-accent">Nutzung.</span>
          </h2>
        </div>
        <EnergyFlowDiagram/>
      </section>

      {/* 5 technical sections */}
      <TechBlock n="03" title="Wärmepumpen & Heizung" icon={Thermometer} items={heatingItems}
        intro="Luftwärmepumpen für Heizung, Warmwasser und mögliche Kühlung. Kombination mit Fußbodenheizung, Pufferspeicher und Raumregelung. Immer im Bezug zu Gebäude, Heizflächen und Temperaturbedarf."/>
      <TechBlock n="04" title="Warmwasser" icon={Droplet} items={hotWaterItems}
        intro="Warmwasser als Teil des Energiekonzepts — mit Speicher, Wärmepumpenanbindung und optionaler PV-Integration."/>
      <TechBlock n="05" title="Kühlung" icon={Snowflake} items={coolingItems}
        intro="Flächenkühlung über die Wärmepumpe. Nicht jedes System ist für jedes Gebäude geeignet — die Prüfung erfolgt im konkreten Projekt."/>
      <TechBlock n="06" title="Photovoltaik" icon={Sun} items={pvItems}
        intro="Eigenverbrauch im Mittelpunkt — kombiniert mit Speicher, Wärmepumpe und optional E-Auto. Konkrete Erträge werden projektabhängig berechnet, nicht pauschal versprochen."/>
      <TechBlock n="07" title="Energiemanagement" icon={Gauge} items={emsItems}
        intro="Monitoring, Lastmanagement, PV-Überschussnutzung und Visualisierung — die Grundlage für datenbasierte Optimierung."/>

      {/* CTA */}
      <section className="site-container section-block">
        <div className="cta-panel">
          <div className="eyebrow mb-5 text-[#5FA637]">Nächster Schritt</div>
          <h2 className="section-title max-w-3xl">
            Energie-Konzept <span className="italic-accent">für Ihr Gebäude.</span>
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/kontakt" className="btn-primary">
              {t("cta.inquiry")} <ArrowRight size={14}/>
            </Link>
            <Link to="/energie-rechner" className="btn-ghost">
              {t("cta.calc")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function TechBlock({ n, title, icon: Icon, items, intro }) {
  return (
    <section className="site-container section-block border-t border-white/10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-5 text-[#5FA637]">{n}</div>
          <div className="flex items-center gap-3 mb-5">
            <Icon size={24} strokeWidth={1.5} className="text-[#5FA637]"/>
            <h2 className="section-title">{title}</h2>
          </div>
          <p className="text-white/70 leading-relaxed">{intro}</p>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {items.map((it, i) => (
              <div key={i} className="bg-[#0A1F38]/65 px-4 py-3 text-sm text-white/80 flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#5FA637] shrink-0"/>
                {it}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function EnergyFlowDiagram() {
  const nodes = [
    { k: "pv", label: "Photovoltaik", icon: Sun, x: 60, y: 100 },
    { k: "ems", label: "Energie-Management", icon: Gauge, x: 320, y: 100 },
    { k: "hp", label: "Wärmepumpe", icon: Thermometer, x: 580, y: 60 },
    { k: "bat", label: "Speicher", icon: Battery, x: 580, y: 140 },
    { k: "heat", label: "Heizung / Warmwasser", icon: Flame, x: 840, y: 100 },
    { k: "load", label: "Hausverbrauch", icon: Zap, x: 320, y: 220 },
    { k: "mon", label: "Monitoring / Optimierung", icon: Gauge, x: 580, y: 220 },
  ];

  const edges = [
    ["pv", "ems", 0], ["ems", "hp", 0.3], ["ems", "bat", 0.6], ["hp", "heat", 0.9],
    ["bat", "heat", 1.2], ["ems", "load", 1.5], ["load", "mon", 1.8], ["mon", "ems", 2.1],
  ];
  const byKey = Object.fromEntries(nodes.map((n) => [n.k, n]));

  return (
    <div className="surface-panel p-5 md:p-8 overflow-x-auto">
      <svg viewBox="0 0 1000 300" className="w-full h-auto min-w-[760px]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="flow-gradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#5FA637" stopOpacity="0"/>
            <stop offset="50%" stopColor="#7DBE4E" stopOpacity="1"/>
            <stop offset="100%" stopColor="#5FA637" stopOpacity="0"/>
          </linearGradient>
          <radialGradient id="node-glow">
            <stop offset="0%" stopColor="#5FA637" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="#5FA637" stopOpacity="0"/>
          </radialGradient>
        </defs>

        {/* edges with animated flow */}
        {edges.map(([a, b, delay], i) => {
          const na = byKey[a], nb = byKey[b];
          const x1 = na.x + 70, y1 = na.y, x2 = nb.x - 70, y2 = nb.y;
          const dx = x2 - x1, dy = y2 - y1;
          const len = Math.sqrt(dx * dx + dy * dy);
          return (
            <g key={i}>
              {/* static base line */}
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#5FA637" strokeWidth="1.2" strokeDasharray="3 4" opacity="0.3"/>
              {/* animated flowing dash */}
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#7DBE4E" strokeWidth="2.5" strokeDasharray="8 20"
                strokeLinecap="round" opacity="0.9">
                <animate attributeName="stroke-dashoffset" from="0" to="-28" dur="1.5s"
                  repeatCount="indefinite" begin={`${delay}s`}/>
              </line>
              {/* arrow head */}
              <circle cx={x2} cy={y2} r="4" fill="#7DBE4E">
                <animate attributeName="r" values="3;6;3" dur="1.5s" repeatCount="indefinite" begin={`${delay}s`}/>
              </circle>
            </g>
          );
        })}

        {/* nodes */}
        {nodes.map((n) => (
          <g key={n.k} transform={`translate(${n.x - 70}, ${n.y - 32})`}>
            <ellipse cx="70" cy="32" rx="90" ry="40" fill="url(#node-glow)" opacity="0.6"/>
            <rect width="140" height="64" fill="#0A1F38" stroke="#5FA637" strokeOpacity="0.5" strokeWidth="1.2" rx="4"/>
            <circle cx="24" cy="32" r="12" fill="#5FA637" fillOpacity="0.2" stroke="#5FA637" strokeWidth="1.2">
              <animate attributeName="fill-opacity" values="0.15;0.35;0.15" dur="2.5s" repeatCount="indefinite"/>
            </circle>
            <text x="46" y="30" fontSize="10" fill="#5FA637" fontFamily="Inter" fontWeight="700" letterSpacing="0.5">
              {n.k.toUpperCase()}
            </text>
            <text x="46" y="46" fontSize="11" fill="#FFFFFF" fontFamily="Inter" fontWeight="500">
              {n.label}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-6 text-xs text-white/50 leading-relaxed max-w-3xl flex items-center gap-3">
        <span className="w-2 h-2 rounded-full bg-[#5FA637] animate-pulse"/>
        <span>
          Vereinfachtes Schema: Photovoltaik → Energie-Management → Wärmepumpe & Speicher → Heizung/Warmwasser.
          Verbrauchsdaten gehen zurück ins Monitoring und optimieren den Betrieb.
        </span>
      </div>
    </div>
  );
}
